/**
 * Language code conversion utilities for HTML lang attributes
 */

// Common ISO 639-3 to ISO 639-1 mappings for HTML lang attribute
const ISO639_3_TO_1_MAP: Record<string, string> = {
  'eng': 'en',  // English
  'deu': 'de',  // German (Deutsch)
  'fra': 'fr',  // French
  'spa': 'es',  // Spanish
  'ita': 'it',  // Italian
  'por': 'pt',  // Portuguese
  'rus': 'ru',  // Russian
  'pol': 'pl',  // Polish
  'nld': 'nl',  // Dutch
  'swe': 'sv',  // Swedish
  'nor': 'no',  // Norwegian
  'dan': 'da',  // Danish
  'jpn': 'ja',  // Japanese
  'kor': 'ko',  // Korean
  'zho': 'zh',  // Chinese
  'ara': 'ar',  // Arabic
  'heb': 'he',  // Hebrew
  'hin': 'hi',  // Hindi
  'tha': 'th',  // Thai
  'vie': 'vi',  // Vietnamese
  'fas': 'fa',  // Persian
  'urd': 'ur',  // Urdu
  'ces': 'cs',  // Czech
  'slk': 'sk',  // Slovak
  'hrv': 'hr',  // Croatian
  'slv': 'sl',  // Slovenian
  'srp': 'sr',  // Serbian
  'bul': 'bg',  // Bulgarian
  'ron': 'ro',  // Romanian
  'hun': 'hu',  // Hungarian
  'fin': 'fi',  // Finnish
  'est': 'et',  // Estonian
  'lav': 'lv',  // Latvian
  'lit': 'lt',  // Lithuanian
  'ell': 'el',  // Greek
  'tur': 'tr',  // Turkish
  'ukr': 'uk',  // Ukrainian
  'bel': 'be',  // Belarusian
  'mkd': 'mk',  // Macedonian
  'alb': 'sq',  // Albanian
  'afr': 'af',  // Afrikaans
  'isl': 'is',  // Icelandic
  'mlt': 'mt',  // Maltese
  'kat': 'ka',  // Georgian
  'hye': 'hy',  // Armenian
  'aze': 'az',  // Azerbaijani
  'bos': 'bs',  // Bosnian
  'cat': 'ca',  // Catalan
  'eus': 'eu',  // Basque
  'glg': 'gl',  // Galician
  'gle': 'ga',  // Irish
  'cym': 'cy',  // Welsh
  'mon': 'mn',  // Mongolian
  'tgl': 'tl',  // Tagalog
  'ind': 'id',  // Indonesian
  'msa': 'ms',  // Malay
  'swa': 'sw',  // Swahili
  'tam': 'ta',  // Tamil
  'kan': 'kn',  // Kannada
  'mar': 'mr',  // Marathi
  'nep': 'ne',  // Nepali
  'pan': 'pa',  // Punjabi
  'sin': 'si',  // Sinhala
  'mya': 'my',  // Myanmar (Burmese)
  'khm': 'km',  // Khmer
  'lao': 'lo',  // Lao
  'ben': 'bn',  // Bengali
  'guj': 'gu',  // Gujarati
  'tel': 'te',  // Telugu
  'mal': 'ml',  // Malayalam
  'ori': 'or',  // Odia
  'asm': 'as',  // Assamese
  'kaz': 'kk',  // Kazakh
  'kir': 'ky',  // Kyrgyz
  'uzb': 'uz',  // Uzbek
  'tgk': 'tg',  // Tajik
  'tuk': 'tk',  // Turkmen
  'pus': 'ps',  // Pashto
  'prs': 'prs', // Dari (no ISO 639-1, keep as is)
  'nob': 'nb',  // Norwegian Bokmål
  'hau': 'ha',  // Hausa
  'kur': 'ku',  // Kurdish
  'ceb': 'ceb', // Cebuano (no ISO 639-1, keep as is)
  'fil': 'tl',  // Filipino (use Tagalog code)
  'hat': 'ht',  // Haitian Creole
  'hmn': 'hmn', // Hmong (no ISO 639-1, keep as is)
  'ibo': 'ig',  // Igbo
  'jav': 'jv',  // Javanese
  'lat': 'la',  // Latin
  'mri': 'mi',  // Māori
  'smo': 'sm',  // Samoan
  'som': 'so',  // Somali
  'sqi': 'sq',  // Albanian (alternative code)
  'amh': 'am',  // Amharic
  'ltz': 'lb',  // Luxembourgish
  'san': 'sa',  // Sanskrit
  'xho': 'xh',  // Xhosa
  'zul': 'zu',  // Zulu
  'yid': 'yi',  // Yiddish
  'yor': 'yo',  // Yoruba
  'uig': 'ug',  // Uyghur
};

/**
 * Convert ISO 639-3 language code to ISO 639-1 for HTML lang attribute
 * Falls back to the original code if no mapping exists
 */
export function convertLanguageCodeForHtml(languageCode?: string): string {
  if (!languageCode) return 'en';

  const code = languageCode.toLowerCase();

  // If it's already a 2-letter code, return as is
  if (code.length === 2) {
    return code;
  }

  // If it's a 3-letter code, try to convert
  if (code.length === 3) {
    return ISO639_3_TO_1_MAP[code] || code;
  }

  // For any other format, return as is (fallback)
  return code;
}

/**
 * Get HTML lang attribute value from SelectedLanguage
 */
export function getHtmlLangFromSelectedLanguage(language?: { id: string; name?: string }): string {
  return convertLanguageCodeForHtml(language?.id);
}
